// services/pagination.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { PaginationParams, PaginationResponse } from './pagination.interface';

@Injectable({
    providedIn: 'root'
})
export class PaginationService {
    private currentPageSubject = new BehaviorSubject<number>(1);
    private pageSizeSubject = new BehaviorSubject<number>(5);
    private searchSubject = new BehaviorSubject<string>('');
    private totalItemsSubject = new BehaviorSubject<number>(0);
    private totalPagesSubject = new BehaviorSubject<number>(0);

    constructor(private http: HttpClient) { }

    getPaginatedData<T>(endpoint: string, params: PaginationParams = {}): Observable<PaginationResponse<T>> {
        let httpParams = new HttpParams();

        // Add pagination parameters
        Object.keys(params).forEach(key => {
            if (params[key] !== undefined && params[key] !== null) {
                httpParams = httpParams.set(key, params[key].toString());
            }
        });

        return this.http.get<PaginationResponse<T>>(endpoint, { params: httpParams });
    }

    // State management methods
    setCurrentPage(page: number) {
        this.currentPageSubject.next(page);
    }

    getCurrentPage(): Observable<number> {
        return this.currentPageSubject.asObservable();
    }

    setPageSize(size: number) {
        this.pageSizeSubject.next(size);
    }

    getPageSize(): Observable<number> {
        return this.pageSizeSubject.asObservable();
    }

    setSearch(search: string) {
        this.searchSubject.next(search);
    }

    getSearch(): Observable<string> {
        return this.searchSubject.asObservable();
    }
    // Total items methods
    setTotalItems(total: number) {
        this.totalItemsSubject.next(total);
    }

    getTotalItems(): Observable<number> {
        return this.totalItemsSubject.asObservable();
    }

    getTotalItemsValue(): number {
        return this.totalItemsSubject.value;
    }

    // Total pages methods
    setTotalPages(total: number) {
        this.totalPagesSubject.next(total);
    }

    getTotalPages(): Observable<number> {
        return this.totalPagesSubject.asObservable();
    }

    getTotalPagesValue(): number {
        return this.totalPagesSubject.value;
    }

    // Utility methods
    reset() {
        this.currentPageSubject.next(1);
        this.pageSizeSubject.next(5);
        this.searchSubject.next('');
        this.totalItemsSubject.next(0);
        this.totalPagesSubject.next(0);
    }

    // Calculate total pages based on total items and page size
    calculateTotalPages(): number {
        const totalItems = this.getTotalItemsValue();
        const pageSize = this.getPageSizeValue();
        return Math.ceil(totalItems / pageSize);
    }

    // Update total pages automatically when total items or page size changes
    updateTotalPages() {
        const totalPages = this.calculateTotalPages();
        this.setTotalPages(totalPages);
    }
    getCurrentPageValue(): number {
        return this.currentPageSubject.value;
    }
    getPageSizeValue(): number {
        return this.pageSizeSubject.value;
    }
    getSearchValue(): string {
        return this.searchSubject.value;
    }
    // Get current pagination state as an object
    getCurrentPaginationState() {
        return {
            currentPage: this.getCurrentPageValue(),
            pageSize: this.getPageSizeValue(),
            search: this.getSearchValue(),
            totalItems: this.getTotalItemsValue(),
            totalPages: this.getTotalPagesValue()
        };
    }
}
